import React, { memo, useMemo } from '../../lib/teact/teact';

import type { ApiChain, ApiSwapActivity, ApiSwapAsset } from '../../api/types';

import { resolveSwapAsset } from '../../global/helpers';
import { getIsActivityPendingForUser } from '../../util/activities';
import { getLifiReceivedToken } from '../../util/swap/lifiHistory';
import { getSwapTransactionIdRows } from '../../util/swap/transactionIds';
import { getExplorerTransactionUrl } from '../../util/url';

import useLang from '../../hooks/useLang';

import InteractiveTextField from '../ui/InteractiveTextField';
import SwapTokensInfo from './SwapTokensInfo';
import TransactionFee from './TransactionFee';

import styles from './SwapActivityInfo.module.scss';

interface OwnProps {
  activity: ApiSwapActivity;
  tokensBySlug?: Record<string, ApiSwapAsset>;
  isSensitiveDataHidden?: boolean;
  selectedExplorerIds?: Partial<Record<ApiChain, string>>;
}

const ONCHAIN_ERROR_STATUSES = new Set(['expired', 'failed']);

function SwapActivityInfo({
  activity,
  tokensBySlug,
  isSensitiveDataHidden,
  selectedExplorerIds,
}: OwnProps) {
  const lang = useLang();

  const {
    from,
    fromAmount,
    to,
    toAmount,
    status,
    networkFee = '0',
    shouldLoadDetails,
    cex,
  } = activity;

  const fromToken = useMemo(() => {
    if (!from || !tokensBySlug) return undefined;
    return resolveSwapAsset(tokensBySlug, from);
  }, [from, tokensBySlug]);

  const toToken = useMemo(() => {
    if (!to || !tokensBySlug) return undefined;
    return getLifiReceivedToken(activity, resolveSwapAsset(tokensBySlug, to));
  }, [activity, to, tokensBySlug]);

  const isPending = getIsActivityPendingForUser(activity);
  const isError = ONCHAIN_ERROR_STATUSES.has(status) || (cex && status === 'failed');

  const transactionIdRows = getSwapTransactionIdRows(activity);

  function renderFee() {
    if (!(Number(networkFee) || shouldLoadDetails) || !fromToken) {
      return undefined;
    }

    const terms = {
      native: networkFee,
    };

    return (
      <TransactionFee
        terms={terms}
        token={fromToken}
        precision={isPending ? 'approximate' : 'exact'}
        isLoading={shouldLoadDetails}
      />
    );
  }

  function renderTransactionIds() {
    if (!transactionIdRows.length) return undefined;

    return transactionIdRows.map(({ label, hash, chain, traceId }) => (
      <div key={`${label}-${hash}`} className={styles.textFieldWrapper}>
        <span className={styles.textFieldLabel}>
          {lang(label)}
        </span>
        <InteractiveTextField
          noSavedAddress
          chain={chain}
          address={hash}
          addressUrl={getExplorerTransactionUrl(chain, { hash, traceId }, undefined, selectedExplorerIds?.[chain])}
          isTransaction
          copyNotification={lang('Transaction ID Copied')}
        />
      </div>
    ));
  }

  return (
    <div className={styles.root}>
      <SwapTokensInfo
        tokenIn={fromToken}
        amountIn={fromAmount}
        tokenOut={toToken}
        amountOut={toAmount}
        isError={isError}
        isSensitiveDataHidden={isSensitiveDataHidden || undefined}
      />

      <div className={styles.infoBlock}>
        {renderFee()}
        {renderTransactionIds()}
      </div>
    </div>
  );
}

export default memo(SwapActivityInfo);
